import { gql, useMutation, useLazyQuery, useQuery } from '@apollo/client';
import { today, type CalendarDate } from '@internationalized/date';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  PaginationDirection,
  TransactionAnomalyPriority as TransactionAnomalyPriorityEnum,
  TransactionCategoryDetailedType,
  type TransactionFilters,
} from '../../../__generated__/globalTypes';
import { useClipboard } from '../../../hooks/useClipboard';
import { CheckIcon, CopyIcon } from '../../../icons';
import logger from '../../../lib/logger';
import { PATHS } from '../../../paths';
import { filterNullsAndUndefined } from '../../../utils/filterUtils';
import { formatCalendarDate, toCurrency } from '../../../utils/formatting';
import Button from '../../base/buttons/Button';
import LoadingIndicator from '../../base/LoadingIndicator';
import NativeSelect from '../../base/select/SelectNative';
import { TableCard } from '../../base/Table';
import TextArea from '../../base/Textarea';
import Tooltip, { TooltipTrigger } from '../../base/Tooltip/Tooltip';

import AnomalyScoreRanges from './AnomalyScoreRanges';
import GlobalStatistics from './GlobalStatistics';
import TransactionPairs from './TransactionPairs';

import type {
  AdminCreateTransactionAnomalyFeedbackMutation,
  AdminCreateTransactionAnomalyFeedbackMutationVariables,
  AdminDeleteTransactionAnomalyFeedbackMutation,
  AdminDeleteTransactionAnomalyFeedbackMutationVariables,
  AdminGetTransactionsForReviewQuery,
  AdminGetTransactionsForReviewQueryVariables,
  AdminGetUserQuery,
  AdminGetUserQueryVariables,
} from './__generated__/TransactionAnomalyFeedback.gql';

// Use the enum values as a const object for easier access
const TransactionAnomalyPriority = {
  P0: TransactionAnomalyPriorityEnum.P0,
  P1: TransactionAnomalyPriorityEnum.P1,
  P2: TransactionAnomalyPriorityEnum.P2,
  P3: TransactionAnomalyPriorityEnum.P3,
  P4: TransactionAnomalyPriorityEnum.P4,
} as const;

type TransactionAnomalyPriority = TransactionAnomalyPriorityEnum;

interface ITransactionWithAnomalyData {
  id: string;
  amount: number;
  date: CalendarDate;
  description: string | null;
  rawJson?: unknown;
  merchantId: string | null;
  merchantName: string | null;
  financialAccountId: string;
  financialAccountName: string;
  anomalyScore: number;
  priority: TransactionAnomalyPriority;
  anomalyDetection: {
    anomalyReasons: string[];
    scoreParts: {
      baseScore: number;
      merchantAdjustment: number;
      timingAdjustment: number;
      financialAccountAdjustment: number;
      finalScore: number;
    };
    featuresJson: unknown;
  } | null;
  anomalyFeedback?: {
    priority: TransactionAnomalyPriority;
    detailedReason?: string | null;
  } | null;
  category: {
    type: string;
    detailedType?: string | null;
  };
  pairedTransaction?: {
    id: string;
    amount: number | string;
    date: CalendarDate;
    description: string | null;
    merchant?: { id: string; name: string } | null;
    financialAccount: { id: string; name: string };
  } | null;
}

const TRANSACTION_FRAGMENT = gql`
  fragment TransactionFragment on Transaction {
    id
    amount
    date
    description
    rawJson
    merchant {
      id
      name
    }
    category {
      id
      type
      detailedType
    }
    financialAccount {
      id
      name
    }
    anomalyDetection {
      id
      anomalyScore
      anomalyReasons
      scoreParts {
        baseScore
        merchantAdjustment
        timingAdjustment
        financialAccountAdjustment
        finalScore
      }
      featuresJson
    }
    anomalyFeedback {
      id
      priority
      detailedReason
    }
    pairedTransaction {
      id
      amount
      date
      description
      merchant {
        id
        name
      }
      financialAccount {
        id
        name
      }
    }
  }
`;

const ADMIN_GET_TRANSACTIONS_FOR_REVIEW = gql`
  ${TRANSACTION_FRAGMENT}
  query AdminGetTransactionsForReview($userId: String!, $pagination: PaginationInput!, $filters: TransactionFilters) {
    adminGetTransactionsForUser(userId: $userId, pagination: $pagination, filters: $filters) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ...TransactionFragment
        }
      }
    }
    adminGetLatestTransactionMerchantStatistics(userId: $userId) {
      id
      merchant {
        id
        name
      }
      startDate
      endDate
      statsJson
    }
  }
`;

const CREATE_TRANSACTION_ANOMALY_FEEDBACK = gql`
  ${TRANSACTION_FRAGMENT}
  mutation AdminCreateTransactionAnomalyFeedback($input: AdminCreateTransactionAnomalyFeedbackInput!) {
    adminCreateTransactionAnomalyFeedback(input: $input) {
      ...TransactionFragment
    }
  }
`;

const DELETE_TRANSACTION_ANOMALY_FEEDBACK = gql`
  ${TRANSACTION_FRAGMENT}
  mutation AdminDeleteTransactionAnomalyFeedback($transactionId: ID!, $userId: ID!) {
    adminDeleteTransactionAnomalyFeedback(transactionId: $transactionId, userId: $userId) {
      ...TransactionFragment
    }
  }
`;

const ADMIN_GET_USER = gql`
  query AdminGetUser($id: ID!) {
    getUser(id: $id) {
      id
      name
      email
    }
  }
`;

/**
 * Copy button component using a plain button element.
 * Note: Using a plain <button> instead of the Button component because:
 * 1. The Button component causes tooltip positioning issues when used inside tooltip descriptions
 * 2. We're applying custom styles directly anyway, so we don't need Button's styling
 */
const CopyButton = ({
  text,
  ariaLabel,
  iconSize = 'h-3 w-3',
  className = '',
}: {
  text: string;
  ariaLabel: string;
  iconSize?: string;
  className?: string;
}) => {
  const { copy, copied } = useClipboard();

  return (
    <button
      type="button"
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        void copy(text);
      }}
      className={`border-0 bg-transparent p-0 shadow-none hover:bg-transparent ${className}`}
      aria-label={ariaLabel}
      style={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }}
    >
      {copied ? <CheckIcon className={iconSize} /> : <CopyIcon className={iconSize} />}
    </button>
  );
};

/**
 * Component to render JSON with a copy button in the upper right
 */
const JsonWithCopyButton = ({ json }: { json: unknown }) => {
  const jsonString = JSON.stringify(json, null, 2);

  return (
    <div className="relative">
      <div className="absolute right-0 top-0 z-10">
        <CopyButton
          text={jsonString}
          ariaLabel="Copy JSON"
          iconSize="h-4 w-4"
          className="flex items-center justify-center rounded p-1.5 text-white hover:bg-white/20"
        />
      </div>
      <pre className="m-0 max-h-96 overflow-auto whitespace-pre-wrap pr-8 text-xs">{jsonString}</pre>
    </div>
  );
};

/**
 * Component to render text with a copy button
 */
const TextWithCopyButton = ({ text }: { text: string }) => {
  return (
    <CopyButton
      text={text}
      ariaLabel="Copy"
      className="flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-600"
    />
  );
};

/**
 * Collapsible section component with toggle
 */
const CollapsibleSection = ({
  title,
  isOpen,
  onToggle,
  children,
  priority,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  priority?: TransactionAnomalyPriority;
}) => {
  const priorityColors: Record<TransactionAnomalyPriority, string> = {
    [TransactionAnomalyPriorityEnum.P0]: 'border-red-500 bg-red-50',
    [TransactionAnomalyPriorityEnum.P1]: 'border-yellow-500 bg-yellow-50',
    [TransactionAnomalyPriorityEnum.P2]: 'border-blue-500 bg-blue-50',
    [TransactionAnomalyPriorityEnum.P3]: 'border-green-500 bg-green-50',
    [TransactionAnomalyPriorityEnum.P4]: 'border-gray-400 bg-gray-50',
  };

  const priorityTextColors: Record<TransactionAnomalyPriority, string> = {
    [TransactionAnomalyPriorityEnum.P0]: 'text-red-900',
    [TransactionAnomalyPriorityEnum.P1]: 'text-yellow-900',
    [TransactionAnomalyPriorityEnum.P2]: 'text-blue-900',
    [TransactionAnomalyPriorityEnum.P3]: 'text-green-900',
    [TransactionAnomalyPriorityEnum.P4]: 'text-gray-700',
  };

  const borderColor = priority ? priorityColors[priority] : 'border-gray-400 bg-gray-50';
  const textColor = priority ? priorityTextColors[priority] : 'text-gray-700';

  return (
    <div className={`rounded-lg border-l-4 ${borderColor} shadow-sm`}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between p-4 text-left"
        aria-expanded={isOpen}
      >
        <h3 className={`text-lg font-semibold ${textColor}`}>{title}</h3>
        <span className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

/**
 * Component to display and edit priority for transaction anomaly feedback
 */
const PriorityCell = ({
  transactionId,
  currentScore,
  existingFeedback,
  onSave,
  onDetailedReasonChange,
  mapDetailedReason,
}: {
  transactionId: string;
  currentScore: number;
  existingFeedback?: {
    priority: TransactionAnomalyPriority;
    detailedReason?: string | null;
  } | null;
  onSave: (input: {
    transactionId: string;
    priority: TransactionAnomalyPriority;
    detailedReason?: string | null;
  }) => Promise<void>;
  onDetailedReasonChange?: (transactionId: string, detailedReason: string) => void;
  mapDetailedReason?: string;
}) => {
  // Determine current priority based on score
  const getPriorityFromScore = (score: number): TransactionAnomalyPriority => {
    if (score === 10) {
      return TransactionAnomalyPriority.P0;
    } else if (score >= 9) {
      return TransactionAnomalyPriority.P1;
    } else if (score >= 8) {
      return TransactionAnomalyPriority.P2;
    } else if (score >= 6) {
      return TransactionAnomalyPriority.P3;
    } else {
      return TransactionAnomalyPriority.P4;
    }
  };

  // Priority is from feedback if it exists, otherwise from score
  const currentPriority = existingFeedback?.priority ?? getPriorityFromScore(currentScore);
  const displayPriority = currentPriority;

  // State for detailedReason - prefer map value (preserves unsaved edits), then existingFeedback, then empty
  const [detailedReason, setDetailedReason] = React.useState<string>(
    mapDetailedReason ?? existingFeedback?.detailedReason ?? ''
  );

  // Update local state when map value or existingFeedback changes, but prefer map value
  React.useEffect(() => {
    const valueToUse = mapDetailedReason ?? existingFeedback?.detailedReason ?? '';
    setDetailedReason(valueToUse);
  }, [mapDetailedReason, existingFeedback?.detailedReason]);

  const handlePriorityChange = (newPriority: TransactionAnomalyPriority) => {
    if (newPriority === currentPriority) {
      return; // No change
    }

    // Fire mutation
    void onSave({
      transactionId,
      priority: newPriority,
      detailedReason: detailedReason || null,
    }).catch((error) => {
      logger.error('Failed to save priority:', error);
    });
  };

  const handleDetailedReasonChange = (newDetailedReason: string) => {
    setDetailedReason(newDetailedReason);
    // Notify parent of the change so it can be saved when checkbox is checked
    if (onDetailedReasonChange) {
      onDetailedReasonChange(transactionId, newDetailedReason);
    }
  };

  return (
    <div className="relative w-full min-w-[100px]">
      <div>
        <TextArea
          value={detailedReason}
          onChange={(value: string) => {
            handleDetailedReasonChange(value);
          }}
          placeholder="Add detailed reason..."
          rows={2}
          className="w-full"
        />
      </div>
      <div className="relative mt-2">
        <NativeSelect
          value={displayPriority}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            const newPriority = e.target.value as TransactionAnomalyPriorityEnum;
            void handlePriorityChange(newPriority);
          }}
          options={[
            { label: 'P0', value: TransactionAnomalyPriority.P0 },
            { label: 'P1', value: TransactionAnomalyPriority.P1 },
            { label: 'P2', value: TransactionAnomalyPriority.P2 },
            { label: 'P3', value: TransactionAnomalyPriority.P3 },
            { label: 'P4', value: TransactionAnomalyPriority.P4 },
          ]}
          className="w-full"
          selectClassName="cursor-pointer"
        />
      </div>
    </div>
  );
};

/**
 * Filters transactions by date range using CalendarDate comparison.
 */
function filterByDateRange<T extends { date: CalendarDate }>(
  transactions: T[],
  startDate: CalendarDate,
  endDate: CalendarDate
): T[] {
  return transactions.filter((t) => {
    const afterStart = t.date.compare(startDate) >= 0;
    const beforeEnd = t.date.compare(endDate) <= 0;
    return afterStart && beforeEnd;
  });
}

/**
 * Component to render a table of transactions
 */
const TransactionsTable = ({
  transactions,
  title,
  merchantStatsForDisplay,
  createTransactionAnomalyFeedback,
  navigate,
  onToggleReviewed,
  onDetailedReasonChange,
  detailedReasonMap,
  userId,
}: {
  transactions: Array<ITransactionWithAnomalyData>;
  title: string;
  merchantStatsForDisplay: Map<string, Record<string, unknown>>;
  createTransactionAnomalyFeedback: ReturnType<
    typeof useMutation<
      AdminCreateTransactionAnomalyFeedbackMutation,
      AdminCreateTransactionAnomalyFeedbackMutationVariables
    >
  >[0];
  navigate: (path: string) => void;
  onToggleReviewed: (transactionId: string, checked: boolean) => Promise<void>;
  onDetailedReasonChange?: (transactionId: string, detailedReason: string) => void;
  detailedReasonMap?: Map<string, string>;
  userId: string;
}) => {
  if (transactions.length === 0) {
    return null;
  }

  return (
    <TableCard.Root className="min-w-180 shrink-0">
      <TableCard.Header title={title} description={`${transactions.length} transactions`} />
      <table aria-label="Transaction statistics table" className="w-full [&_td]:select-text [&_td_*]:select-text">
        <thead>
          <tr>
            <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Reviewed</th>
            <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Priority</th>
            <th className="px-6 py-3 text-right text-sm font-medium text-gray-600">Anomaly score</th>
            <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Anomaly reasons</th>
            <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Date</th>
            <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Description</th>
            <th className="px-6 py-3 text-right text-sm font-medium text-gray-600">Amount</th>
            <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Global stats</th>
            <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Merchant global stats</th>
            <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Merchant stats</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((transaction) => {
            const evaluationFeatures = transaction.anomalyDetection?.featuresJson;

            // Determine background color based on priority
            const getUrgencyBackground = () => {
              const priority = transaction.priority;
              if (priority === TransactionAnomalyPriority.P0) {
                return 'bg-red-50'; // P0: Red
              } else if (priority === TransactionAnomalyPriority.P1) {
                return 'bg-yellow-50'; // P1: Yellow
              } else if (priority === TransactionAnomalyPriority.P2) {
                return 'bg-blue-50'; // P2: Blue
              } else if (priority === TransactionAnomalyPriority.P3) {
                return 'bg-green-50'; // P3: Green
              } else {
                return 'bg-gray-50'; // P4: Gray
              }
            };

            // Extract evaluation data from featuresJson
            // Handle null values from JSON by converting to undefined
            const getNumberOrUndefined = (value: unknown): number | undefined => {
              return typeof value === 'number' ? value : undefined;
            };

            const features = evaluationFeatures as Record<string, unknown> | undefined;
            const globalPercentile = features ? getNumberOrUndefined(features.percentile) : undefined;
            const globalPercentVariationFromMedian = features
              ? getNumberOrUndefined(features.percent_deviation_from_median)
              : undefined;
            const merchantPercentile = features ? getNumberOrUndefined(features.merchant_percentile) : undefined;
            const merchantPercentVariationFromMedian = features
              ? getNumberOrUndefined(features.merchant_percent_deviation_from_median)
              : undefined;
            const merchantCount = features ? getNumberOrUndefined(features.merchant_count) : undefined;

            // Convert day_of_month_match and financial_account_match from int to string
            const dayOfMonthMatchInt = features ? (features.day_of_month_match as number | undefined) : undefined;
            const dayOfMonthMatch = dayOfMonthMatchInt === 1 ? 'Yes' : dayOfMonthMatchInt === -1 ? 'No' : 'n/a';

            const financialAccountMatchInt = features
              ? (features.financial_account_match as number | undefined)
              : undefined;
            const financialAccountMatch =
              financialAccountMatchInt === 1 ? 'Yes' : financialAccountMatchInt === -1 ? 'No' : 'n/a';

            // Get merchant stats from merchantStatsMap
            const merchantStats = transaction.merchantId
              ? merchantStatsForDisplay.get(transaction.merchantId)
              : undefined;

            // Check Apollo cache for reviewed status
            const isReviewed = !!transaction.anomalyFeedback;

            return (
              <tr
                key={transaction.id}
                data-transaction-id={transaction.id}
                className={`**:select-text h-16 ${getUrgencyBackground()}`}
              >
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={isReviewed}
                    onChange={(e) => {
                      void onToggleReviewed(transaction.id, e.target.checked);
                    }}
                    className="h-4 w-4 cursor-pointer"
                    aria-label={`Mark transaction ${transaction.id} as reviewed`}
                  />
                </td>
                <td className="min-w-[140px] px-6 py-4">
                  <PriorityCell
                    transactionId={transaction.id}
                    currentScore={transaction.anomalyScore}
                    existingFeedback={
                      transaction.anomalyFeedback
                        ? {
                            priority: transaction.anomalyFeedback.priority,
                            detailedReason: transaction.anomalyFeedback.detailedReason,
                          }
                        : undefined
                    }
                    onSave={async (input) => {
                      await createTransactionAnomalyFeedback({
                        variables: {
                          input: {
                            userId: userId,
                            transactionIds: [input.transactionId],
                            priority: input.priority,
                            detailedReason: input.detailedReason,
                          },
                        },
                      });
                    }}
                    onDetailedReasonChange={onDetailedReasonChange}
                    mapDetailedReason={detailedReasonMap?.get(transaction.id)}
                  />
                </td>
                <td className="select-text px-6 py-4">
                  <div className="flex justify-end">
                    <span className="text-base font-medium">
                      {transaction.anomalyScore !== undefined ? transaction.anomalyScore.toFixed(2) : 'N/A'}
                    </span>
                  </div>
                </td>
                <td className="select-text px-6 py-4">
                  {(() => {
                    // Check if transaction is negative
                    const transactionAmount =
                      typeof transaction.amount === 'string' ? parseFloat(transaction.amount) : transaction.amount;
                    const isNegative = transactionAmount < 0;

                    // Check if transaction is part of a pair
                    const isPaired = !!transaction.pairedTransaction;

                    // Show special reasons for negative or paired transactions
                    if (isNegative) {
                      return (
                        <div className="flex flex-col gap-1 text-xs">
                          <div className="rounded bg-gray-100 px-2 py-1">Negative Transactions are not scored</div>
                        </div>
                      );
                    }

                    if (isPaired) {
                      return (
                        <div className="flex flex-col gap-1 text-xs">
                          <div className="rounded bg-gray-100 px-2 py-1">Part of debit/credit pair</div>
                        </div>
                      );
                    }

                    // Show normal anomaly reasons
                    if (
                      transaction.anomalyDetection?.anomalyReasons &&
                      transaction.anomalyDetection.anomalyReasons.length > 0
                    ) {
                      return (
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-col gap-1 text-xs">
                            {transaction.anomalyDetection.anomalyReasons.map((reason: string, idx: number) => (
                              <div key={idx} className="rounded bg-gray-100 px-2 py-1">
                                {reason}
                              </div>
                            ))}
                          </div>
                          {transaction.anomalyDetection.scoreParts && (
                            <div className="flex flex-col gap-0.5 border-t border-gray-200 pt-2 text-xs text-gray-600">
                              <div className="mb-1 font-semibold">Score breakdown:</div>
                              <div className="flex justify-between">
                                <span>Base score:</span>
                                <span className="font-medium">
                                  {transaction.anomalyDetection.scoreParts.baseScore.toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Merchant adj:</span>
                                <span
                                  className={`font-medium ${
                                    transaction.anomalyDetection.scoreParts.merchantAdjustment > 0
                                      ? 'text-red-600'
                                      : transaction.anomalyDetection.scoreParts.merchantAdjustment < 0
                                        ? 'text-green-600'
                                        : ''
                                  }`}
                                >
                                  {transaction.anomalyDetection.scoreParts.merchantAdjustment > 0 ? '+' : ''}
                                  {transaction.anomalyDetection.scoreParts.merchantAdjustment.toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Timing adj:</span>
                                <span
                                  className={`font-medium ${
                                    transaction.anomalyDetection.scoreParts.timingAdjustment > 0
                                      ? 'text-red-600'
                                      : transaction.anomalyDetection.scoreParts.timingAdjustment < 0
                                        ? 'text-green-600'
                                        : ''
                                  }`}
                                >
                                  {transaction.anomalyDetection.scoreParts.timingAdjustment > 0 ? '+' : ''}
                                  {transaction.anomalyDetection.scoreParts.timingAdjustment.toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Account adj:</span>
                                <span
                                  className={`font-medium ${
                                    transaction.anomalyDetection.scoreParts.financialAccountAdjustment > 0
                                      ? 'text-red-600'
                                      : transaction.anomalyDetection.scoreParts.financialAccountAdjustment < 0
                                        ? 'text-green-600'
                                        : ''
                                  }`}
                                >
                                  {transaction.anomalyDetection.scoreParts.financialAccountAdjustment > 0 ? '+' : ''}
                                  {transaction.anomalyDetection.scoreParts.financialAccountAdjustment.toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold">
                                <span>Final score:</span>
                                <span>{transaction.anomalyDetection.scoreParts.finalScore.toFixed(2)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Check if there's no anomaly detection (not processed yet)
                    // If anomalyDetection is null/undefined, the transaction hasn't been processed
                    if (!transaction.anomalyDetection) {
                      return (
                        <div className="flex flex-col gap-1 text-xs">
                          <div className="rounded bg-gray-100 px-2 py-1">Not processed yet</div>
                        </div>
                      );
                    }

                    return <span className="text-gray-400">N/A</span>;
                  })()}
                </td>
                <td className="select-text px-6 py-4">
                  {(() => {
                    // Check if we have the data loaded for this transaction
                    const rawJson = transaction.rawJson;
                    const filteredJson = rawJson ? filterNullsAndUndefined(rawJson) : undefined;
                    const hasValidJson =
                      rawJson !== null && rawJson !== undefined && filteredJson !== undefined && filteredJson !== null;

                    return (
                      <Tooltip
                        key={`tooltip-${transaction.id}-${hasValidJson ? 'has-json' : 'no-json'}`}
                        title="Raw JSON"
                        description={
                          hasValidJson ? <JsonWithCopyButton json={filteredJson} /> : 'No raw JSON data available'
                        }
                        placement="right"
                        wide
                        isDisabled={!hasValidJson}
                      >
                        <TooltipTrigger className="text-primary text-base font-medium">
                          {formatCalendarDate(transaction.date)}
                        </TooltipTrigger>
                      </Tooltip>
                    );
                  })()}
                </td>
                <td className="select-text px-6 py-4" style={{ userSelect: 'text' }}>
                  <div className="flex flex-col gap-1" style={{ userSelect: 'text' }}>
                    <div className="flex items-center gap-2" style={{ userSelect: 'text' }}>
                      <div className="text-primary select-text text-base" style={{ userSelect: 'text' }}>
                        {transaction.description || 'N/A'}
                      </div>
                    </div>
                    <div
                      className="flex select-text flex-col gap-0.5 text-xs text-gray-500"
                      style={{ userSelect: 'text' }}
                    >
                      <div className="flex items-center gap-1">
                        <span className="font-medium">TransactionId:</span>
                        <span className="select-text">{transaction.id}</span>
                        <TextWithCopyButton text={transaction.id} />
                      </div>
                      {transaction.merchantId && (
                        <>
                          <div>
                            <span className="font-medium">MerchantId:</span>{' '}
                            <Button
                              color="link-color"
                              size="xs"
                              className="p-0 text-xs"
                              onClick={() => {
                                if (transaction.merchantId) {
                                  void navigate(PATHS.MERCHANT(transaction.merchantId));
                                }
                              }}
                            >
                              {transaction.merchantId}
                            </Button>
                          </div>
                          {transaction.merchantName && (
                            <div>
                              <span className="font-medium">MerchantName:</span> {transaction.merchantName}
                            </div>
                          )}
                        </>
                      )}
                      {transaction.financialAccountId && (
                        <div>
                          <span className="font-medium">FinancialAccount:</span>{' '}
                          <Button
                            color="link-color"
                            size="xs"
                            className="p-0 text-xs"
                            onClick={() => void navigate(PATHS.ACCOUNT(transaction.financialAccountId))}
                          >
                            {transaction.financialAccountName || transaction.financialAccountId}
                          </Button>
                        </div>
                      )}
                      {transaction.category && (
                        <div>
                          <span className="font-medium">Category:</span>{' '}
                          <span className="text-gray-500">
                            {transaction.category.type}
                            {transaction.category.detailedType ? ` - ${transaction.category.detailedType}` : ''}
                          </span>
                        </div>
                      )}
                      {transaction.pairedTransaction && (
                        <div className="mt-2 border-t border-gray-200 pt-2">
                          <div className="mb-1 text-xs font-semibold text-gray-600">Paired transaction:</div>
                          <div className="flex flex-col gap-0.5">
                            <div className="text-sm font-medium">
                              {transaction.pairedTransaction.description || 'N/A'}
                            </div>
                            <div className="flex select-text flex-col gap-0.5 text-xs text-gray-500">
                              <div>
                                <span className="font-medium">Date: </span>
                                {formatCalendarDate(transaction.pairedTransaction.date)}
                              </div>
                              <div>
                                <span className="font-medium">Amount: </span>
                                {toCurrency(
                                  typeof transaction.pairedTransaction.amount === 'string'
                                    ? parseFloat(transaction.pairedTransaction.amount)
                                    : transaction.pairedTransaction.amount
                                )}
                              </div>
                              <div>
                                <span className="font-medium">Account: </span>
                                {transaction.pairedTransaction.financialAccount.name}
                              </div>
                              {transaction.pairedTransaction.merchant?.name && (
                                <div>
                                  <span className="font-medium">Merchant: </span>
                                  {transaction.pairedTransaction.merchant.name}
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <span className="font-medium">ID: </span>
                                <span className="select-text">{transaction.pairedTransaction.id}</span>
                                <TextWithCopyButton text={transaction.pairedTransaction.id} />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="select-text px-6 py-4">
                  <div className="flex justify-end">
                    <span className="text-base font-medium">
                      {toCurrency(
                        typeof transaction.amount === 'string' ? parseFloat(transaction.amount) : transaction.amount
                      )}
                    </span>
                  </div>
                </td>
                <td className="select-text px-6 py-4">
                  {globalPercentile != null && globalPercentVariationFromMedian != null ? (
                    <div className="flex flex-col gap-1 text-sm">
                      <div>
                        <span className="text-gray-600">Percentile: </span>
                        <span className="font-medium">{globalPercentile.toFixed(1)}%</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Δ Median %: </span>
                        <span className="font-medium">{globalPercentVariationFromMedian.toFixed(1)}%</span>
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </td>
                <td className="select-text px-6 py-4">
                  {merchantStats ? (
                    <div className="flex flex-col gap-1 text-sm">
                      <div>
                        <span className="text-gray-600">Median: </span>
                        <span className="font-medium">{toCurrency((merchantStats.median as number) ?? 0)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Count: </span>
                        <span className="font-medium">{merchantStats.count as number}</span>
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-400">No merchant stats</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {merchantPercentile != null && merchantPercentVariationFromMedian != null ? (
                    <div className="flex flex-col gap-1 text-sm">
                      <div>
                        <span className="text-gray-600">Percentile: </span>
                        <span className="font-medium">{merchantPercentile.toFixed(1)}%</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Δ Median %: </span>
                        <span className="font-medium">{merchantPercentVariationFromMedian.toFixed(1)}%</span>
                      </div>
                      {merchantCount !== undefined && (
                        <div>
                          <span className="text-gray-600">Count: </span>
                          <span className="font-medium">{merchantCount}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600">Day of month match: </span>
                        <span className="font-medium">{dayOfMonthMatch}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Financial account match: </span>
                        <span className="font-medium">{financialAccountMatch}</span>
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-400">No merchant stats</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableCard.Root>
  );
};

const TransactionAnomalyFeedback = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  // Fetch user information
  const { data: userData } = useQuery<AdminGetUserQuery, AdminGetUserQueryVariables>(ADMIN_GET_USER, {
    variables: { id: userId ?? '' },
    skip: !userId,
  });

  // For admin (specific user), use lazy query to fetch all pages
  const [fetchAdminTransactions, { loading: adminLoading, error: adminError }] = useLazyQuery<
    AdminGetTransactionsForReviewQuery,
    AdminGetTransactionsForReviewQueryVariables
  >(ADMIN_GET_TRANSACTIONS_FOR_REVIEW);

  const [allAdminTransactions, setAllAdminTransactions] = React.useState<
    AdminGetTransactionsForReviewQuery['adminGetTransactionsForUser']['edges'][number]['node'][]
  >([]);
  const [adminMerchantStats, setAdminMerchantStats] = React.useState<
    AdminGetTransactionsForReviewQuery['adminGetLatestTransactionMerchantStatistics']
  >([]);
  const [isFetchingAllPages, setIsFetchingAllPages] = React.useState(false);

  // Fetch all pages when userId is available
  React.useEffect(() => {
    if (!userId) {
      return;
    }

    const fetchAllTransactions = async () => {
      setIsFetchingAllPages(true);
      const allTransactions: AdminGetTransactionsForReviewQuery['adminGetTransactionsForUser']['edges'][number]['node'][] =
        [];
      let currentCursor: string | null = null;
      let hasNextPage = true;
      let merchantStats: AdminGetTransactionsForReviewQuery['adminGetLatestTransactionMerchantStatistics'] = [];

      const todayDate = today('UTC');
      const oneMonthAgo = todayDate.subtract({ months: 1 });

      const filters: TransactionFilters = {
        startDate: oneMonthAgo.toString() as unknown as CalendarDate,
        endDate: todayDate.toString() as unknown as CalendarDate,
      };

      while (hasNextPage) {
        const result = await fetchAdminTransactions({
          variables: {
            userId,
            pagination: {
              cursor: currentCursor,
              limit: 500, // MAX_LIMIT
              direction: PaginationDirection.Forward,
            },
            filters,
          },
        });

        if (result.error) {
          setIsFetchingAllPages(false);
          return;
        }

        const data = result.data;
        if (data) {
          const edges = data.adminGetTransactionsForUser?.edges ?? [];
          allTransactions.push(...edges.map((edge) => edge.node));

          // Store merchant stats from first page
          if (merchantStats.length === 0) {
            merchantStats = data.adminGetLatestTransactionMerchantStatistics ?? [];
          }

          hasNextPage = data.adminGetTransactionsForUser?.pageInfo?.hasNextPage ?? false;
          currentCursor = data.adminGetTransactionsForUser?.pageInfo?.endCursor ?? null;
        } else {
          hasNextPage = false;
        }
      }

      setAllAdminTransactions(allTransactions);
      setAdminMerchantStats(merchantStats);
      setIsFetchingAllPages(false);
    };

    void fetchAllTransactions();
  }, [userId, fetchAdminTransactions]);

  // Determine which data to use - memoize to avoid recreating on every render
  const transactionsData = React.useMemo(() => {
    return {
      adminGetTransactionsForUser: {
        edges: allAdminTransactions.map((node) => ({ node })),
      },
      adminGetLatestTransactionMerchantStatistics: adminMerchantStats,
    } as AdminGetTransactionsForReviewQuery;
  }, [allAdminTransactions, adminMerchantStats]);

  const loading = adminLoading || isFetchingAllPages;
  const error = adminError;

  // State to manage visibility of each priority section
  const [priorityVisibility, setPriorityVisibility] = React.useState<Record<TransactionAnomalyPriority, boolean>>({
    P0: true,
    P1: false,
    P2: false,
    P3: false,
    P4: false,
  });

  const togglePriorityVisibility = React.useCallback((priority: TransactionAnomalyPriority) => {
    setPriorityVisibility((prev) => ({
      ...prev,
      [priority]: !prev[priority],
    }));
  }, []);

  // State to manage visibility of transaction pairs section
  const [pairsVisibility, setPairsVisibility] = React.useState(false);

  const togglePairsVisibility = React.useCallback(() => {
    setPairsVisibility((prev) => !prev);
  }, []);

  // State to store detailedReason values for transactions (for saving when checkbox is checked)
  const [detailedReasonMap, setDetailedReasonMap] = React.useState<Map<string, string>>(new Map());

  // Initialize detailedReasonMap with existing values from transactions
  // Only update with saved feedback from server, preserve any unsaved local edits
  React.useEffect(() => {
    const transactionsToProcess = transactionsData.adminGetTransactionsForUser.edges.map(
      (edge: { node: AdminGetTransactionsForReviewQuery['adminGetTransactionsForUser']['edges'][number]['node'] }) =>
        edge.node
    );

    setDetailedReasonMap((prev) => {
      const newMap = new Map(prev);
      for (const transaction of transactionsToProcess) {
        // Only set if there's saved feedback - this preserves unsaved edits
        if (transaction.anomalyFeedback?.detailedReason) {
          newMap.set(transaction.id, transaction.anomalyFeedback.detailedReason);
        }
      }
      return newMap;
    });
  }, [transactionsData]);

  const handleDetailedReasonChange = React.useCallback((transactionId: string, detailedReason: string) => {
    setDetailedReasonMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(transactionId, detailedReason);
      return newMap;
    });
  }, []);

  const [createTransactionAnomalyFeedback] = useMutation<
    AdminCreateTransactionAnomalyFeedbackMutation,
    AdminCreateTransactionAnomalyFeedbackMutationVariables
  >(CREATE_TRANSACTION_ANOMALY_FEEDBACK, {
    update: (_cache, { data }) => {
      if (data?.adminCreateTransactionAnomalyFeedback) {
        // Update local state with the updated transactions
        setAllAdminTransactions((prev) => {
          const updatedMap = new Map(prev.map((t) => [t.id, t]));
          for (const updatedTransaction of data.adminCreateTransactionAnomalyFeedback) {
            updatedMap.set(updatedTransaction.id, updatedTransaction);
          }
          return Array.from(updatedMap.values());
        });
      }
    },
  });

  const [deleteTransactionAnomalyFeedback] = useMutation<
    AdminDeleteTransactionAnomalyFeedbackMutation,
    AdminDeleteTransactionAnomalyFeedbackMutationVariables
  >(DELETE_TRANSACTION_ANOMALY_FEEDBACK, {
    update: (_cache, { data }) => {
      if (data?.adminDeleteTransactionAnomalyFeedback) {
        // Update local state with the updated transaction
        setAllAdminTransactions((prev) => {
          const updatedMap = new Map(prev.map((t) => [t.id, t]));
          updatedMap.set(data.adminDeleteTransactionAnomalyFeedback.id, data.adminDeleteTransactionAnomalyFeedback);
          return Array.from(updatedMap.values());
        });
      }
    },
  });

  // Helper function to get priority from score (same logic as PriorityCell)
  const getPriorityFromScore = React.useCallback((score: number): TransactionAnomalyPriority => {
    if (score === 10) {
      return TransactionAnomalyPriority.P0;
    } else if (score >= 9) {
      return TransactionAnomalyPriority.P1;
    } else if (score >= 8) {
      return TransactionAnomalyPriority.P2;
    } else if (score >= 6) {
      return TransactionAnomalyPriority.P3;
    } else {
      return TransactionAnomalyPriority.P4;
    }
  }, []);

  // Handler to toggle reviewed status for a transaction
  const handleToggleReviewed = React.useCallback(
    async (transactionId: string, checked: boolean) => {
      if (!userId) {
        return;
      }

      if (checked) {
        // Find the transaction to get its score
        const transactionsToSearch = transactionsData.adminGetTransactionsForUser.edges.map(
          (edge: {
            node: AdminGetTransactionsForReviewQuery['adminGetTransactionsForUser']['edges'][number]['node'];
          }) => edge.node
        );
        const transaction = transactionsToSearch.find((t) => t.id === transactionId);

        if (!transaction || transaction.anomalyFeedback) {
          return;
        }

        const isCreditCardPayment =
          transaction.category.detailedType === TransactionCategoryDetailedType.LoanPaymentsCreditCardPayment;

        // Get anomaly score from GraphQL, or 0 if credit card payment
        let anomalyScore = 0;
        if (!isCreditCardPayment && transaction.anomalyDetection) {
          anomalyScore = transaction.anomalyDetection.anomalyScore;
        }

        // Determine priority from score
        const priority = getPriorityFromScore(anomalyScore);

        // Get the current detailedReason from the map, or use null
        const detailedReason = detailedReasonMap.get(transactionId) || null;

        // Fire mutation
        await createTransactionAnomalyFeedback({
          variables: {
            input: {
              userId: userId,
              transactionIds: [transaction.id],
              priority: priority,
              detailedReason: detailedReason,
            },
          },
        });
      } else {
        // Delete feedback when unchecked
        await deleteTransactionAnomalyFeedback({
          variables: {
            transactionId,
            userId: userId,
          },
        });
      }
    },
    [
      transactionsData,
      createTransactionAnomalyFeedback,
      deleteTransactionAnomalyFeedback,
      getPriorityFromScore,
      detailedReasonMap,
      userId,
    ]
  );

  // Handler to mark all unreviewed transactions in a priority as reviewed
  const handleMarkAllAsReviewed = React.useCallback(
    async (transactionIds: string[], priority: TransactionAnomalyPriority) => {
      if (!userId || transactionIds.length === 0) {
        return;
      }

      await createTransactionAnomalyFeedback({
        variables: {
          input: {
            userId: userId,
            transactionIds,
            priority: priority,
            detailedReason: null,
          },
        },
      });
    },
    [createTransactionAnomalyFeedback, userId]
  );

  // Extract data from query result
  const transactions = React.useMemo(() => {
    return transactionsData.adminGetTransactionsForUser.edges.map(
      (edge: { node: AdminGetTransactionsForReviewQuery['adminGetTransactionsForUser']['edges'][number]['node'] }) =>
        edge.node
    );
  }, [transactionsData]);

  const merchantStatsData = React.useMemo(() => {
    return transactionsData.adminGetLatestTransactionMerchantStatistics || [];
  }, [transactionsData]);

  // Create a map of merchant stats by merchant ID
  const merchantStatsMap = React.useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    for (const merchantStat of merchantStatsData) {
      if (merchantStat.merchant?.id) {
        map.set(merchantStat.merchant.id, merchantStat.statsJson);
      }
    }
    return map;
  }, [merchantStatsData]);

  const { groupedByPriority, merchantStatsMap: merchantStatsForDisplay } = React.useMemo(() => {
    const groupingStartTime = performance.now();
    // eslint-disable-next-line no-console
    console.log(`[${new Date().toISOString()}] Grouping transactions useMemo START`, {
      transactionCount: transactions?.length ?? 0,
      timestamp: groupingStartTime,
    });

    if (!transactions || transactions.length === 0) {
      return {
        unreviewed: [],
        reviewed: [],
        merchantStatsMap: new Map<string, Record<string, unknown>>(),
      };
    }

    const todayDate = today('UTC');
    const oneMonthAgo = todayDate.subtract({ months: 1 });

    // Get all transactions from last 1 month (including negative amounts for display)
    const recentAll = filterByDateRange(transactions, oneMonthAgo, todayDate);

    // Get all recent transactions for display (including negative)
    // Include anomaly scores from GraphQL
    const transactionsWithScores = recentAll
      .map((t) => {
        const isCreditCardPayment =
          t.category.detailedType === TransactionCategoryDetailedType.LoanPaymentsCreditCardPayment;

        // Get anomaly score from GraphQL, or 0 if credit card payment
        let anomalyScore = 0;
        if (!isCreditCardPayment && t.anomalyDetection) {
          anomalyScore = t.anomalyDetection.anomalyScore;
        }

        // Determine priority: from feedback if exists, otherwise from score
        const priorityFromFeedback = t.anomalyFeedback?.priority;
        const priorityFromScore = getPriorityFromScore(anomalyScore);
        const priority = priorityFromFeedback ?? priorityFromScore;

        return {
          ...t,
          amount: typeof t.amount === 'string' ? parseFloat(t.amount) : t.amount,
          merchantId: t.merchant?.id ?? null,
          merchantName: t.merchant?.name ?? null,
          financialAccountId: t.financialAccount.id,
          financialAccountName: t.financialAccount.name,
          anomalyScore,
          anomalyDetection: t.anomalyDetection ?? null,
          priority,
        };
      })
      .filter((t) => t.anomalyDetection !== null && t.anomalyDetection !== undefined);

    // Group by priority, then by reviewed/unreviewed
    const groupedByPriority: Record<
      TransactionAnomalyPriority,
      { unreviewed: typeof transactionsWithScores; reviewed: typeof transactionsWithScores }
    > = {
      P0: { unreviewed: [], reviewed: [] },
      P1: { unreviewed: [], reviewed: [] },
      P2: { unreviewed: [], reviewed: [] },
      P3: { unreviewed: [], reviewed: [] },
      P4: { unreviewed: [], reviewed: [] },
    };

    // Group by priority, then by reviewed/unreviewed, maintaining original order
    // Transactions are added in the order they appear in transactionsWithScores
    // This ensures that when priority or reviewed status changes, transactions
    // maintain their relative positions within their groups
    for (const transaction of transactionsWithScores) {
      // Check Apollo cache for reviewed status
      const isReviewed = !!transaction.anomalyFeedback;
      const priority = transaction.priority;

      if (isReviewed) {
        groupedByPriority[priority].reviewed.push(transaction);
      } else {
        groupedByPriority[priority].unreviewed.push(transaction);
      }
    }

    const groupingEndTime = performance.now();
    const groupingDuration = groupingEndTime - groupingStartTime;
    // eslint-disable-next-line no-console
    console.log(`[${new Date().toISOString()}] Grouping transactions useMemo END`, {
      transactionCount: transactions.length,
      duration: `${groupingDuration}ms`,
      timestamp: groupingEndTime,
      groupedByPriority,
    });

    return {
      groupedByPriority,
      merchantStatsMap: merchantStatsMap,
    };
  }, [transactions, merchantStatsMap, getPriorityFromScore]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingIndicator size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-red-600">Error loading transactions: {error.message}</div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-error-primary text-xl">Error: User ID is required</div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-6 overflow-y-auto p-6">
      <div className="flex flex-col gap-4">
        <div className="text-3xl font-bold">Transaction anomaly feedback</div>
        {userData?.getUser && (
          <div className="text-lg text-gray-700">
            Reviewing transactions for: <span className="font-semibold">{userData.getUser.name}</span> (
            {userData.getUser.email})
          </div>
        )}
        <div className="text-sm text-gray-600">
          Review and provide feedback on transaction anomaly scores from the last month.
        </div>
      </div>

      <AnomalyScoreRanges />

      <GlobalStatistics userId={userId} />

      {/* Grouped by Priority */}
      {groupedByPriority &&
        (
          [
            TransactionAnomalyPriority.P0,
            TransactionAnomalyPriority.P1,
            TransactionAnomalyPriority.P2,
            TransactionAnomalyPriority.P3,
            TransactionAnomalyPriority.P4,
          ] as TransactionAnomalyPriority[]
        ).map((priority) => {
          const unreviewed = groupedByPriority[priority].unreviewed;
          const reviewed = groupedByPriority[priority].reviewed;
          const hasTransactions = unreviewed.length > 0 || reviewed.length > 0;

          if (!hasTransactions) {
            return null;
          }

          const priorityLabels: Record<TransactionAnomalyPriority, string> = {
            [TransactionAnomalyPriorityEnum.P0]: 'P0: Urgent (Score: 10)',
            [TransactionAnomalyPriorityEnum.P1]: 'P1: High priority (Score: ≥9)',
            [TransactionAnomalyPriorityEnum.P2]: 'P2: Medium priority (Score: ≥8)',
            [TransactionAnomalyPriorityEnum.P3]: 'P3: Low priority (Score: ≥6)',
            [TransactionAnomalyPriorityEnum.P4]: 'P4: Not an anomaly (Score: <6)',
          };

          return (
            <CollapsibleSection
              key={priority}
              title={`${priorityLabels[priority]} (${unreviewed.length} unreviewed, ${reviewed.length} reviewed)`}
              isOpen={priorityVisibility[priority]}
              onToggle={() => togglePriorityVisibility(priority)}
              priority={priority}
            >
              <div className="flex flex-col gap-6">
                {/* Unreviewed table for this priority */}
                {unreviewed.length > 0 && (
                  <div className="flex flex-col gap-4">
                    <TransactionsTable
                      transactions={unreviewed}
                      title={`${priority} - Unreviewed`}
                      merchantStatsForDisplay={merchantStatsForDisplay}
                      createTransactionAnomalyFeedback={createTransactionAnomalyFeedback}
                      navigate={navigate}
                      onToggleReviewed={handleToggleReviewed}
                      onDetailedReasonChange={handleDetailedReasonChange}
                      detailedReasonMap={detailedReasonMap}
                      userId={userId}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={() => {
                          const transactionIds = unreviewed.map((t) => t.id);
                          void handleMarkAllAsReviewed(transactionIds, priority);
                        }}
                      >
                        Mark all {priority}'s as reviewed
                      </Button>
                    </div>
                  </div>
                )}

                {/* Reviewed table for this priority */}
                {reviewed.length > 0 && (
                  <TransactionsTable
                    transactions={reviewed}
                    title={`${priority} - Reviewed`}
                    merchantStatsForDisplay={merchantStatsForDisplay}
                    createTransactionAnomalyFeedback={createTransactionAnomalyFeedback}
                    navigate={navigate}
                    onToggleReviewed={handleToggleReviewed}
                    onDetailedReasonChange={handleDetailedReasonChange}
                    detailedReasonMap={detailedReasonMap}
                    userId={userId}
                  />
                )}
              </div>
            </CollapsibleSection>
          );
        })}

      {/* Transaction Pairs */}
      <CollapsibleSection title="Transaction pairs" isOpen={pairsVisibility} onToggle={togglePairsVisibility}>
        <TransactionPairs userId={userId} />
      </CollapsibleSection>
    </div>
  );
};

export default TransactionAnomalyFeedback;
